<template>
  <template v-if="isVisible">
    <WizardModal
      v-model="currentStepIndex"
      v-model:show-modal="showModal"
      :steps="wizardSteps"
      :modal-title="modalTitle"
      elem-id="mensagens-programadas-lote-wizard"
      size="modalMd"
      :show-pre-wizard="editMode"
      :allow-step-click="true"
      custom-buttons
      :back-label="backButtonLabel"
      :back-icon="backButtonIcon"
      :next-label="nextButtonLabel"
      :next-icon="nextButtonIcon"
      :disable-next="disableNextButton"
      @finish="handleScheduleMessages"
      @cancel="handleCancel"
      @close="handleModalClose"
      @dismiss="handleModalClose"
      @before-step-change="(handleBeforeStepChange as any)"
    >
    <!-- Pre-wizard: Edit Mode (View Operation Details) -->
    <template #pre-wizard>
    <div class="edit-mode-content">
      <div v-if="isLoading" class="loading-container">
        <spinner style="width: 3rem; height: 3rem" />
        <p>{{ t('Carregando...') }}</p>
      </div>
      <div v-else-if="operationDetails">
        <div class="info-boxes-grid">
          <div class="info-box-wrapper">
            <div class="info-box aqua-box">
              <div class="info-box-icon">
                <i class="pi pi-calendar"></i>
              </div>
              <div class="info-box-content">
                <span class="info-box-text">{{ t('Período') }}</span>
                <span class="info-box-number">{{ formatDate(operationDetails.periodo_inicio) }} - {{ formatDate(operationDetails.periodo_fim) }}</span>
              </div>
            </div>
          </div>
          <div class="info-box-wrapper">
            <div class="info-box green-box">
              <div class="info-box-icon">
                <i class="pi pi-users"></i>
              </div>
              <div class="info-box-content">
                <span class="info-box-text">{{ t('Quantidade de Clientes') }}</span>
                <span class="info-box-number">{{ operationDetails.quantidade_clientes }}</span>
              </div>
            </div>
          </div>
          <div class="info-box-wrapper">
            <div class="info-box yellow-box">
              <div class="info-box-icon">
                <i class="pi pi-check"></i>
              </div>
              <div class="info-box-content">
                <span class="info-box-text">{{ t('Mensagens Agendadas') }}</span>
                <span class="info-box-number">{{ operationDetails.mensagens_agendadas || 0 }}</span>
              </div>
            </div>
          </div>
          <div class="info-box-wrapper">
            <div class="info-box red-box">
              <div class="info-box-icon">
                <i class="pi pi-times"></i>
              </div>
              <div class="info-box-content">
                <span class="info-box-text">{{ t('Mensagens com Erro') }}</span>
                <span class="info-box-number">{{ operationDetails.mensagens_com_erro || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="franchises-section">
          <label class="section-label">{{ t('Franquias:') }}</label>
          <div class="franchise-chips">
            <span
              v-for="(franquia, index) in operationDetails.franquias"
              :key="index"
              class="franchise-chip"
            >
              {{ franquia }}
            </span>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- Step 0: Filter Form -->
    <template #step-0>
    <div class="step-content">
      <MensagensProgramadasLoteForm
        ref="formRef"
        :is-searching="isSearching"
        @search="handleSearchCustomers"
      />
    </div>
    </template>

    <!-- Step 1: Customer Preview -->
    <template #step-1>
      <div class="step-content">
        <inline-message severity="info" :closable="false">
          <strong>{{ t('Clientes Encontrados:') }}</strong> {{ customers.length }} {{ t('cliente(s) que atendem aos critérios selecionados') }}
        </inline-message>
        <div class="table-container customers-list">
          <div class="customers-list-header">
            <span class="col col-id">{{ t('ID') }}</span>
            <span class="col col-name">{{ t('Nome') }}</span>
            <span class="col">{{ t('Franquia') }}</span>
            <span class="col">{{ t('Localidade') }}</span>
            <span class="col">{{ t('Telefone') }}</span>
            <span class="col">{{ t('Último Compromisso') }}</span>
            <span class="col">{{ t('Último Contato') }}</span>
          </div>
          <div
            v-for="customer in customers"
            :key="customer.cliente_id"
            class="customers-list-row"
            :data-testid="`mensagens-programadas-lote-preview-row-${customer.cliente_id}`"
          >
            <span class="col col-id">{{ customer.cliente_id }}</span>
            <span class="col col-name">{{ customer.cliente_nome }}</span>
            <span class="col">{{ customer.franquia }}</span>
            <span class="col">{{ customer.localidade || t('N/A') }}</span>
            <span class="col">{{ formatPhone(customer) }}</span>
            <span class="col">{{ formatDate(customer.ultimo_compromisso) }}</span>
            <span class="col">{{ formatDate(customer.ultimo_contato) }}</span>
          </div>
          <div v-if="customers.length === 0" class="customers-list-empty">
            {{ t('Nenhum cliente encontrado') }}
          </div>
        </div>
      </div>
    </template>

    <!-- Step 2: Device Assignment -->
    <template #step-2>
      <div class="step-content">
        <inline-message severity="warn" :closable="false">
          <strong>{{ t('Atenção:') }}</strong> {{ t('Selecione um dispositivo WhatsApp para cada cliente') }}
        </inline-message>
        <div class="table-container customers-list">
          <div class="customers-list-header">
            <span class="col col-name">{{ t('Cliente') }}</span>
            <span class="col">{{ t('Telefone') }}</span>
            <span class="col col-device">{{ t('Dispositivo WhatsApp') }}</span>
          </div>
          <div
            v-for="customer in customers"
            :key="customer.cliente_id"
            class="customers-list-row"
            :data-testid="`mensagens-programadas-lote-device-row-${customer.cliente_id}`"
          >
            <span class="col col-name">{{ customer.cliente_nome }}</span>
            <span class="col">{{ formatPhone(customer) }}</span>
            <span class="col col-device">
              <select-list
                v-model="deviceAssignments[customer.cliente_id]"
                :itens-select="whatsappDeviceOptions"
                :placeholder="t('Selecione um dispositivo')"
                :elem-id="`device-select-${customer.cliente_id}`"
                :data-testid="`mensagens-programadas-lote-device-select-${customer.cliente_id}`"
                fluid
              />
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 3: Confirmation and Scheduling -->
    <template #step-3>
      <div class="step-content">
        <div v-if="!isScheduling && !schedulingComplete">
          <inline-message severity="warn" :closable="false">
            <strong>{{ t('Confirmação:') }}</strong> {{ t('Você está prestes a agendar {count} mensagem(s).', { count: customers.length }) }}
            {{ t('As mensagens serão enviadas automaticamente nos horários agendados.') }}
          </inline-message>
          <div class="summary-box">
            <h4>{{ t('Resumo da Operação:') }}</h4>
            <ul>
              <li><strong>{{ t('Período:') }}</strong> {{ formatDate(searchParams.dataInicio) }} - {{ formatDate(searchParams.dataFim) }}</li>
              <li><strong>{{ t('Quantidade de clientes:') }}</strong> {{ customers.length }}</li>
              <li><strong>{{ t('Template:') }}</strong> {{ searchParams.template?.template_name }}</li>
            </ul>
          </div>
        </div>

        <div v-if="isScheduling" class="scheduling-container">
          <spinner style="width: 3rem; height: 3rem" />
          <h4 style="margin-top: 20px;">{{ t('Agendando mensagens...') }}</h4>
          <p>{{ t('Processando') }} {{ customers.length }} {{ t('mensagem(s)') }}</p>
        </div>

        <div v-if="schedulingComplete" class="completion-container">
          <i class="pi pi-check-circle success-icon" v-if="schedulingResults.success_count > 0"></i>
          <i class="pi pi-exclamation-circle error-icon" v-else></i>
          <h3 style="margin-top: 20px;">
            {{ schedulingResults.success_count > 0 ? t('Concluído!') : t('Erro!') }}
          </h3>
          <div class="results-grid">
            <div class="result-box success-box">
              <div class="result-icon">
                <i class="pi pi-check"></i>
              </div>
              <div class="result-content">
                <span class="result-label">{{ t('Sucesso') }}</span>
                <span class="result-value">{{ schedulingResults.success_count }}</span>
              </div>
            </div>
            <div class="result-box error-box">
              <div class="result-icon">
                <i class="pi pi-times"></i>
              </div>
              <div class="result-content">
                <span class="result-label">{{ t('Erro') }}</span>
                <span class="result-value">{{ schedulingResults.error_count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    </WizardModal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import MensagensProgramadasLoteForm from './MensagensProgramadasLoteForm.vue';
import { useMensagensProgramadasLote } from '@/composables/useMensagensProgramadasLote';
import { useMensagensProgramadas } from '@/composables/useMensagensProgramadas';
import { useFiltro } from '@/composables/useFiltro';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { useDateFormatter } from '@/composables/useDateFormatter';

type Id = number | string;

interface MessageTemplate {
  template_id: number | string;
  template_name?: string;
  template_content?: string;
}

interface Customer {
  cliente_id: number;
  cliente_nome?: string;
  cliente_franquia_id?: number;
  cliente_ddi?: string;
  cliente_ddd?: string;
  cliente_telefone?: string;
  franquia?: string;
  localidade?: string;
  ultimo_compromisso?: string;
  ultimo_contato?: string;
}

interface WhatsAppDevice {
  id: number | string;
  display_name?: string;
}

interface OperationDetails {
  periodo_inicio?: string;
  periodo_fim?: string;
  quantidade_clientes?: number;
  mensagens_agendadas?: number;
  mensagens_com_erro?: number;
  franquias?: string[];
}

interface SearchParams {
  dataInicio: string;
  dataFim: string;
  quantidade: number;
  template: MessageTemplate | null;
}

interface SchedulingResults {
  success_count: number;
  error_count: number;
}

interface ModalOpenPayload {
  editId?: Id | null;
}

interface FormInstance {
  isFormValid: boolean;
  handleSearch: () => void;
}

interface BeforeStepChangePayload {
  from: number;
  to: number;
}

const { t } = useI18n();
const { formatDate: formatDateI18n } = useDateFormatter();
const { getSelectedFranchises } = useFiltro();

// State - visibility controlled by eventBus
const isVisible = ref<boolean>(false);
const editId = ref<Id | null>(null);

// Composables
const { getFilteredCustomers, scheduleBulkMessages, getBulkOperationById } = useMensagensProgramadasLote();
const { fetchWhatsAppDevices } = useMensagensProgramadas();

// Data
const showModal = ref<boolean>(false);
const currentStepIndex = ref<number>(0); // 0-based indexing for WizardModal
const isSearching = ref<boolean>(false);
const isScheduling = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const schedulingComplete = ref<boolean>(false);
const formRef = ref<FormInstance | null>(null);
const editMode = ref<boolean>(false);
const operationDetails = ref<OperationDetails | null>(null);
const searchParams = ref<SearchParams>({
  dataInicio: '',
  dataFim: '',
  quantidade: 0,
  template: null,
});
const customers = ref<Customer[]>([]);
const whatsappDevices = ref<WhatsAppDevice[]>([]);
const deviceAssignments = ref<Record<number, Id>>({});
const schedulingResults = ref<SchedulingResults>({
  success_count: 0,
  error_count: 0,
});

// Wizard steps configuration
const wizardSteps = computed(() => [
  {
    title: t('Escolha de template'),
    validate: (): boolean => {
      // Step 0: form must be valid to enable search button
      return formRef.value?.isFormValid ?? false;
    },
  },
  {
    title: t('Clientes Encontrados'),
    validate: (): boolean => customers.value.length > 0,
  },
  {
    title: t('Atribuir Dispositivos'),
    validate: (): boolean => allDevicesAssigned.value,
  },
  {
    title: t('Agendar'),
    validate: (): boolean => allDevicesAssigned.value && !isScheduling.value,
  },
]);

// Computed
const modalTitle = computed<string>(() => {
  if (editMode.value) {
    return t('Detalhes da Operação em Lote');
  }
  return t('Agendamento em Massa');
});

const allDevicesAssigned = computed<boolean>(() => {
  return customers.value.every(customer => deviceAssignments.value[customer.cliente_id]);
});

const whatsappDeviceOptions = computed(() => {
  return whatsappDevices.value.map(d => ({ ID: d.id, Name: d.display_name || String(d.id) }));
});

// Button configuration for WizardModal (function-based)
const backButtonLabel = computed(() => {
  return (step: number): string => {
    if (step === 0) return t('Cancelar');
    return t('Voltar');
  };
});

const backButtonIcon = computed(() => {
  return (step: number): string => {
    if (step === 0) return 'pi pi-times';
    return 'pi pi-arrow-left';
  };
});

const nextButtonLabel = computed(() => {
  return (step: number): string => {
    if (step === 0) return t('Buscar Clientes');
    if (step === 1) return t('Próximo');
    if (step === 2) return t('Próximo');
    if (step === 3) {
      if (isScheduling.value) return t('Agendando...');
      return t('Confirmar e Agendar');
    }
    return t('Próximo');
  };
});

const nextButtonIcon = computed(() => {
  return (step: number): string => {
    if (step === 0) return 'pi pi-search';
    if (step === 1) return 'pi pi-arrow-right';
    if (step === 2) return 'pi pi-calendar-plus';
    if (step === 3) return 'pi pi-check';
    return 'pi pi-arrow-right';
  };
});

// Methods
const handleCancel = (): void => {
  log('MensagensProgramadasLoteModal', 'handleCancel', 'Wizard cancelled');
  handleClose();
};

const handleModalClose = (): void => {
  log('MensagensProgramadasLoteModal', 'handleModalClose', 'Modal closing');
  handleClose();
};

const handleClose = (): void => {
  if (isScheduling.value) {
    return; // Don't allow closing while scheduling
  }

  log('MensagensProgramadasLoteModal', 'handleClose', 'Closing modal');
  showModal.value = false;
  isVisible.value = false;

  if (schedulingComplete.value) {
    eventBus.emit('mensagens-programadas-lote.saved');
  }

  // Reset after animation
  setTimeout(() => {
    resetModal();
    editId.value = null;
  }, 300);
};

const resetModal = (): void => {
  currentStepIndex.value = 0;
  isSearching.value = false;
  isScheduling.value = false;
  schedulingComplete.value = false;
  editMode.value = false;
  operationDetails.value = null;
  searchParams.value = {
    dataInicio: '',
    dataFim: '',
    quantidade: 0,
    template: null,
  };
  customers.value = [];
  deviceAssignments.value = {};
  schedulingResults.value = {
    success_count: 0,
    error_count: 0,
  };
};

const loadOperationDetails = async (id: number): Promise<void> => {
  log('MensagensProgramadasLoteModal', 'loadOperationDetails', 'Loading operation details', id);
  isLoading.value = true;

  try {
    const details = await getBulkOperationById(id) as OperationDetails;
    operationDetails.value = details;
    log('MensagensProgramadasLoteModal', 'loadOperationDetails', 'Details loaded', details);
  } catch (error) {
    log('MensagensProgramadasLoteModal', 'loadOperationDetails', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar detalhes da operação') + ': ' + message);
    handleClose();
  } finally {
    isLoading.value = false;
  }
};

const loadWhatsAppDevices = async (): Promise<void> => {
  try {
    const devices = await fetchWhatsAppDevices() as WhatsAppDevice[];
    whatsappDevices.value = devices;
    log('MensagensProgramadasLoteModal', 'loadWhatsAppDevices', 'Loaded devices', devices);
  } catch (error) {
    log('MensagensProgramadasLoteModal', 'loadWhatsAppDevices', 'Error', error);
  }
};

const convertDateFormat = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  // parts[0] = day, parts[1] = month, parts[2] = year
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const handleSearchCustomers = async (params: SearchParams): Promise<void> => {
  log('MensagensProgramadasLoteModal', 'handleSearchCustomers', 'Searching customers', params);
  isSearching.value = true;
  searchParams.value = params;

  try {
    // Get franchise IDs from filter
    const franchisesObj = getSelectedFranchises() as Record<string, unknown>;
    const franchiseIds = Object.keys(franchisesObj).map(id => parseInt(id, 10));

    log('MensagensProgramadasLoteModal', 'handleSearchCustomers', 'Franchise IDs', franchiseIds);

    const dataInicioConverted = convertDateFormat(params.dataInicio);
    const dataFimConverted = convertDateFormat(params.dataFim);

    log('MensagensProgramadasLoteModal', 'handleSearchCustomers', 'Converted dates', {
      original_inicio: params.dataInicio,
      converted_inicio: dataInicioConverted,
      original_fim: params.dataFim,
      converted_fim: dataFimConverted,
    });

    const result = await getFilteredCustomers(
      franchiseIds,
      dataInicioConverted,
      dataFimConverted,
      params.quantidade,
    ) as { customers?: Customer[] };

    customers.value = result.customers || [];
    log('MensagensProgramadasLoteModal', 'handleSearchCustomers', 'Found customers', customers.value.length);

    if (customers.value.length === 0) {
      eventBus.emit('globalMessage.warning', t('Nenhum cliente encontrado com os critérios selecionados.'));
    } else {
      // Auto-assign first WhatsApp device to all customers
      if (whatsappDevices.value.length > 0) {
        const firstDeviceId = whatsappDevices.value[0].id;
        customers.value.forEach(customer => {
          deviceAssignments.value[customer.cliente_id] = firstDeviceId;
        });
        log('MensagensProgramadasLoteModal', 'handleSearchCustomers', 'Auto-assigned first device to all customers', {
          deviceId: firstDeviceId,
          customerCount: customers.value.length,
        });
      }
      // Manually advance to next step after successful search
      await nextTick();
      currentStepIndex.value = 1;
    }
  } catch (error) {
    log('MensagensProgramadasLoteModal', 'handleSearchCustomers', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao buscar clientes') + ': ' + message);
  } finally {
    isSearching.value = false;
  }
};

const handleScheduleMessages = async (): Promise<void> => {
  log('MensagensProgramadasLoteModal', 'handleScheduleMessages', 'Starting bulk scheduling');

  if (!allDevicesAssigned.value) {
    eventBus.emit('globalMessage.error', t('Por favor, selecione um dispositivo WhatsApp para todos os clientes.'));
    return;
  }

  isScheduling.value = true;

  try {
    const template = searchParams.value.template;
    if (!template) {
      throw new Error('Template not selected');
    }

    // Prepare messages array. franquia_id is not on the contract — the backend
    // derives the franchise from whatsappfranquia_id, so we don't send it on the wire.
    const messages = customers.value.map(customer => ({
      cliente_id: Number(customer.cliente_id),
      template_id: Number(template.template_id),
      whatsappfranquia_id: Number(deviceAssignments.value[customer.cliente_id]),
    }));

    const currentDatetime = new Date().toISOString();

    // Get franchise IDs from filter
    const franchisesObj = getSelectedFranchises() as Record<string, unknown>;
    const franchiseIds = Object.keys(franchisesObj).map(id => parseInt(id, 10));

    // Prepare bulk data
    const bulkData = {
      periodo_inicio: convertDateFormat(searchParams.value.dataInicio),
      periodo_fim: convertDateFormat(searchParams.value.dataFim),
      franquias: franchiseIds,
      template_id: Number(template.template_id),
    };

    log('MensagensProgramadasLoteModal', 'scheduleMessages', 'Converted dates', {
      original_inicio: searchParams.value.dataInicio,
      converted_inicio: bulkData.periodo_inicio,
      original_fim: searchParams.value.dataFim,
      converted_fim: bulkData.periodo_fim,
    });

    // Prepare customers data for audit trail (contract requires cliente_franquia_id
    // for franchise scoping on the backend audit log).
    const customersData = customers.value.map(c => ({
      cliente_id: Number(c.cliente_id),
      cliente_nome: c.cliente_nome ?? '',
      cliente_franquia_id: Number(c.cliente_franquia_id),
    }));

    const result = await scheduleBulkMessages(messages, currentDatetime, bulkData, customersData) as SchedulingResults;

    schedulingResults.value = {
      success_count: result.success_count || 0,
      error_count: result.error_count || 0,
    };

    log('MensagensProgramadasLoteModal', 'scheduleMessages', 'Scheduling complete', result);
    schedulingComplete.value = true;
  } catch (error) {
    log('MensagensProgramadasLoteModal', 'scheduleMessages', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao agendar mensagens em lote') + ': ' + message);
  } finally {
    isScheduling.value = false;
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return t('N/A');
  try {
    const date = new Date(dateString);
    return formatDateI18n(date);
  } catch (e) {
    return dateString;
  }
};

const formatPhone = (customer: Customer | null | undefined): string => {
  if (!customer) return '';
  const ddi = customer.cliente_ddi || '';
  const ddd = customer.cliente_ddd || '';
  const telefone = customer.cliente_telefone || '';
  return `${ddi} ${ddd} ${telefone}`.trim();
};

const disableNextButton = computed(() => {
  return (step: number): boolean => {
    // Step 0: Disable if form is invalid or searching
    if (step === 0) {
      return !formRef.value?.isFormValid || isSearching.value;
    }

    // Step 1: Disable if no customers found
    if (step === 1) {
      return customers.value.length === 0;
    }

    // Step 2: Disable if not all devices assigned
    if (step === 2) {
      return !allDevicesAssigned.value;
    }

    // Step 3: Disable if scheduling in progress
    if (step === 3) {
      return isScheduling.value;
    }

    return false;
  };
});

const handleBeforeStepChange = ({ from, to }: BeforeStepChangePayload): boolean => {
  log('MensagensProgramadasLoteModal', 'handleBeforeStepChange', 'Step change requested', { from, to });

  // Always allow backward navigation
  if (to < from) {
    return true;
  }

  // Validate forward navigation based on current step
  // Step 0 → Step 1: Must have valid form and trigger search
  if (from === 0 && to === 1) {
    // Check form validity
    if (!formRef.value?.isFormValid) {
      log('MensagensProgramadasLoteModal', 'handleBeforeStepChange', 'Form invalid, preventing step change');
      return false;
    }

    // If already searching, prevent duplicate search
    if (isSearching.value) {
      return false;
    }

    // Trigger search
    if (formRef.value && formRef.value.handleSearch) {
      formRef.value.handleSearch();
    }
    // Prevent automatic advancement - handleSearchCustomers will advance after successful search
    return false;
  }

  // Step 1 → Step 2: Must have customers
  if (from === 1 && to === 2) {
    if (customers.value.length === 0) {
      log('MensagensProgramadasLoteModal', 'handleBeforeStepChange', 'No customers, preventing step change');
      return false;
    }
    return true;
  }

  // Step 2 → Step 3: Must have all devices assigned
  if (from === 2 && to === 3) {
    if (!allDevicesAssigned.value) {
      log('MensagensProgramadasLoteModal', 'handleBeforeStepChange', 'Not all devices assigned, preventing step change');
      return false;
    }
    return true;
  }

  // Step 3 → Step 4: Should not be possible (step 3 is last step before finish)
  if (from === 3 && to > 3) {
    log('MensagensProgramadasLoteModal', 'handleBeforeStepChange', 'Cannot go beyond step 3');
    return false;
  }

  // Allow all other step changes (should not happen, but safe default)
  return true;
};

// EventBus handlers
const handleOpen = (data?: ModalOpenPayload): void => {
  log('MensagensProgramadasLoteModal', 'handleOpen', 'Opening modal via eventBus', data);
  editId.value = data?.editId || null;
  isVisible.value = true;

  if (editId.value) {
    // Edit mode
    editMode.value = true;
    showModal.value = true;
    const idNum = typeof editId.value === 'string' ? parseInt(editId.value, 10) : editId.value;
    loadOperationDetails(idNum);
  } else {
    // Create mode
    editMode.value = false;
    resetModal();
    showModal.value = true;
    loadWhatsAppDevices();
  }
};

// Lifecycle
onMounted(() => {
  log('MensagensProgramadasLoteModal', 'onMounted', 'Modal mounted, listening for events');
  eventBus.on('modal.mensagens-programadas-lote.open', handleOpen);
});

onBeforeUnmount(() => {
  log('MensagensProgramadasLoteModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.mensagens-programadas-lote.open', handleOpen);
});
</script>

<style scoped>
/* Loading Container */
.loading-container {
  text-align: center;
  padding: 40px;
}

/* Scheduling Container */
.scheduling-container {
  text-align: center;
  padding: 40px;
}

/* Completion Container */
.completion-container {
  text-align: center;
  padding: 40px;
}

.success-icon {
  font-size: 5rem;
  color: var(--green-500);
}

.error-icon {
  font-size: 5rem;
  color: var(--red-500);
}

/* Info Boxes Grid */
.info-boxes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.info-box-wrapper {
  flex: 1 1 calc(50% - 8px);
  min-width: 250px;
}

.info-box {
  display: flex;
  min-height: 90px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.info-box-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  font-size: 45px;
  flex-shrink: 0;
}

.info-box-content {
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}

.info-box-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.info-box-number {
  font-weight: bold;
  font-size: 20px;
  color: #333;
}

/* Info Box Color Variants */
.aqua-box .info-box-icon {
  background-color: #00c0ef;
  color: #fff;
}

.green-box .info-box-icon {
  background-color: #00a65a;
  color: #fff;
}

.yellow-box .info-box-icon {
  background-color: #f39c12;
  color: #fff;
}

.red-box .info-box-icon {
  background-color: #dd4b39;
  color: #fff;
}

/* Results Grid */
.results-grid {
  display: flex;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.result-box {
  flex: 1 1 calc(50% - 10px);
  min-width: 250px;
  display: flex;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  font-size: 35px;
  flex-shrink: 0;
}

.result-content {
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.result-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.result-value {
  font-weight: bold;
  font-size: 24px;
  color: #333;
}

.success-box .result-icon {
  background-color: #00a65a;
  color: #fff;
}

.error-box .result-icon {
  background-color: #dd4b39;
  color: #fff;
}

/* Summary Box */
.summary-box {
  padding: 20px;
  margin: 20px 0;
  background-color: #f5f5f5;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
}

.summary-box h4 {
  margin-top: 0;
  margin-bottom: 15px;
}

.summary-box ul {
  margin: 0;
  padding-left: 20px;
}

.summary-box li {
  margin-bottom: 8px;
}

/* Franchises Section */
.franchises-section {
  margin-top: 20px;
}

.section-label {
  font-weight: 600;
  margin-bottom: 10px;
  display: block;
}

.franchise-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.franchise-chip {
  display: inline-block;
  padding: 6px 12px;
  background-color: #3c8dbc;
  color: #fff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
}

/* Table Container */
.table-container {
  margin-top: 15px;
}

/* Customers list (replaces PrimeVue DataTable in wizard steps) */
.customers-list {
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 6px;
  max-height: 400px;
  overflow-y: auto;
}

.customers-list-header,
.customers-list-row {
  display: grid;
  grid-template-columns: 70px 1.4fr 1fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 10px 12px;
  align-items: center;
}

.customers-list-header {
  font-weight: 600;
  background-color: #f9fafb;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
  position: sticky;
  top: 0;
  z-index: 1;
}

.customers-list-row {
  border-bottom: 1px solid var(--surface-border, #f1f5f9);
}

.customers-list-row:nth-child(even) {
  background-color: #fafafa;
}

.customers-list-row:last-child {
  border-bottom: none;
}

.customers-list-empty {
  text-align: center;
  padding: 20px;
}

.col {
  font-size: 0.875rem;
  color: var(--text-color, #1f2937);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-id {
  font-variant-numeric: tabular-nums;
}

.col-name {
  font-weight: 500;
}
</style>
